import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TimetableCard } from "@/components/TimetableCard";
import { EmailCard } from "@/components/EmailCard";
import { NotifyCard } from "@/components/NotifyCard";
import { KitCard } from "@/components/KitCard";
import { YearGroupPicker, JobRolePicker } from "@/components/YearGroup";
import { MeetingsCard } from "@/components/MeetingsCard";
import { useEdition } from "@/lib/edition";

const STEPS = [
  { key: "year", title: "What year are you in?", sub: "Revision packs, AI marking and Ask Daybreyk pitch everything to your level." },
  { key: "timetable", title: "Upload your school timetable", sub: "A photo or screenshot of Week A (and Week B if you have one). Daybreyk reads it for you." },
  { key: "kit", title: "Set your kit list", sub: "Tell Daybreyk what to pack for which lessons (e.g. Games → PE kit), then tap Save." },
  { key: "email", title: "Get the 07:00 brief by email", sub: "Choose where to send it, or switch it off." },
  { key: "push", title: "Turn on phone alerts", sub: "A notification at 07:00 and a kit reminder at 19:00." },
];
const CORP_STEPS = [
  { key: "year", title: "What's your job role?", sub: "Briefs, learning packs and Ask Daybreyk use this for context." },
  { key: "timetable", title: "Add your work schedule", sub: "Import your Outlook or Google calendar link, or add regular meetings by hand." },
  { key: "kit", title: "Set your bring-to-work checklist", sub: "Things to remember for certain meetings or days (e.g. Client visit → laptop charger, ID badge), then tap Save." },
  { key: "email", title: "Get the 07:00 brief by email", sub: "Choose where to send it, or switch it off." },
  { key: "push", title: "Turn on phone alerts", sub: "A notification at 07:00 on workdays and a reminder at 19:00 the evening before." },
];

const StepHead = ({ n, done, title, sub }) => (
  <div className="flex items-start gap-3">
    {done ? <CheckCircle2 data-testid={`step-done-${n}`} className="w-7 h-7 text-teal-600 shrink-0" /> : <Circle className="w-7 h-7 text-slate-300 shrink-0" />}
    <div><p className="font-mono text-xs uppercase tracking-widest text-slate-500">Step {n}</p><h2 className="text-base md:text-lg font-semibold text-slate-900">{title}</h2><p className="text-sm text-slate-600">{sub}</p></div>
  </div>
);

const EmailConfirm = ({ onDone }) => {
  const save = async (enabled) => {
    try {
      const { data } = await api.get("/settings/email");
      await api.put("/settings/email", { enabled, email_to: data?.email_to });
      toast.success(enabled ? `Briefs will go to ${data?.email_to}` : "Email briefs turned off");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <button data-testid="welcome-email-yes" onClick={() => save(true)} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-black hover:bg-slate-700">Yes, email me</button>
      <button data-testid="welcome-email-no" onClick={() => save(false)} className="rounded-xl border border-slate-200 bg-surface px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">No thanks</button>
    </div>
  );
};

export default function Welcome() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [ob, setOb] = useState(null);
  const refresh = useCallback(() => {
    api.get("/onboarding").then((r) => setOb(r.data ?? null)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const finish = async () => {
    try { const { data } = await api.post("/onboarding/complete"); setUser(data); navigate("/", { replace: true }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const { corp, w } = useEdition();
  const steps = corp ? CORP_STEPS : STEPS;
  const s = ob?.steps ?? {};
  const total = ob?.total ?? STEPS.length;
  const body = {
    year: corp ? <JobRolePicker onSaved={refresh} /> : <YearGroupPicker onSaved={refresh} />,
    timetable: corp ? <MeetingsCard /> : <TimetableCard />,
    kit: <KitCard />,
    email: <><EmailConfirm onDone={refresh} /><EmailCard /></>,
    push: <NotifyCard />,
  };

  return (
    <main data-testid="welcome-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-widest text-teal-700">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900">Set up your Daybreyk</h1>
      <p className="mt-3 text-slate-600">{w("A few quick steps to get your school day sorted.", "A few quick steps to get your workday planned.")} Skip any of them and come back later from Settings.</p>
      <div className="mt-6 h-2 rounded-full bg-slate-200 overflow-hidden"><div data-testid="welcome-progress" className="h-full bg-teal-500 transition-[width] duration-500" style={{ width: `${((ob?.done ?? 0) / total) * 100}%` }} /></div>
      <p data-testid="welcome-progress-text" className="mt-2 text-sm text-slate-600">{ob?.done ?? 0} of {total} done</p>
      <ol className="mt-8 space-y-6">
        {steps.map((st, i) => (
          <li key={st.key} data-testid={`welcome-step-${st.key}`} className="rounded-3xl bg-surface/70 border border-slate-200 p-4 sm:p-6 space-y-4">
            <StepHead n={i + 1} done={s[st.key]} title={st.title} sub={st.sub} />
            {body[st.key]}
          </li>
        ))}
      </ol>
      <div className="sticky bottom-0 mt-8 pt-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:pb-4 bg-gradient-to-t from-surface-2 via-surface-2 to-transparent">
        <button data-testid="welcome-finish" onClick={finish} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-brand px-6 py-4 text-base font-semibold text-white hover:bg-brand-dark transition-colors">
          {(ob?.done ?? 0) >= total ? "All set! Go to my brief" : "Finish for now"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </main>
  );
}
